function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
/**
 * Silent rolling diagnostics for problem feedback.
 *
 * Captures recent failed API calls, uncaught JS errors, and connectivity details so support can
 * triage reports without asking the user for anything more. Nothing here is rendered in the
 * feedback UI — the snapshot is attached only when a problem report is submitted.
 *
 * Deliberately omits request/response bodies, headers, tokens, and query values.
 */
import axios from 'axios';
export const MAX_NETWORK_FAILURES = 10;
export const MAX_JS_ERRORS = 10;
export const MAX_MESSAGE_LENGTH = 240;
export const MAX_URL_LENGTH = 300;
export const MAX_STACK_LENGTH = 800;
export const MAX_DIAGNOSTICS_LENGTH = 12000;
const networkFailures = [];
const jsErrors = [];
let listenersInstalled = false;
let axiosRequestInterceptorId = null;
let axiosResponseInterceptorId = null;
const toTrimmedString = value => value === undefined || value === null ? '' : String(value).trim();
const truncate = (value, maxLength) => {
  const text = toTrimmedString(value);
  if (!text) {
    return '';
  }
  if (text.length <= maxLength) {
    return text;
  }
  return `${text.slice(0, maxLength - 1)}…`;
};

/**
 * Keep the path for triage, drop query/hash values that may carry tokens or PII.
 * Hash-router apps keep the hash path (without its query) because that is the real route.
 */
export const sanitizeUrl = (rawUrl = '') => {
  const text = toTrimmedString(rawUrl);
  if (!text) {
    return '';
  }
  try {
    const parsed = new URL(text, 'https://feedback.invalid');
    if (parsed.hash && parsed.hash.startsWith('#/')) {
      const hashBody = parsed.hash.slice(1);
      const [hashPath] = hashBody.split('?');
      const hashHadQuery = hashBody.includes('?');
      return truncate(`#${hashPath || '/'}${hashHadQuery ? '?…' : ''}`, MAX_URL_LENGTH);
    }
    const path = `${parsed.pathname || '/'}${parsed.search ? '?…' : ''}`;
    return truncate(path, MAX_URL_LENGTH);
  } catch (_unused) {
    const hashIndex = text.indexOf('#/');
    if (hashIndex >= 0) {
      const hashBody = text.slice(hashIndex + 1);
      const [hashPath] = hashBody.split('?');
      const hashHadQuery = hashBody.includes('?');
      return truncate(`#${hashPath || '/'}${hashHadQuery ? '?…' : ''}`, MAX_URL_LENGTH);
    }
    const withoutHash = text.split('#')[0];
    const [path] = withoutHash.split('?');
    const sanitized = withoutHash.includes('?') ? `${path}?…` : path;
    return truncate(sanitized, MAX_URL_LENGTH);
  }
};
const pushBounded = (list, entry, max) => {
  list.push(entry);
  while (list.length > max) {
    list.shift();
  }
};
export const clearFeedbackDiagnostics = () => {
  networkFailures.length = 0;
  jsErrors.length = 0;
};
export const recordNetworkFailure = (error = {}) => {
  var _error$response, _config$metadata, _error$response2;
  const config = error.config || {};
  const responseData = (_error$response = error.response) === null || _error$response === void 0 ? void 0 : _error$response.data;
  let message = toTrimmedString(error.message);
  let traceId = '';
  if (typeof responseData === 'string' && responseData) {
    message = responseData;
  } else if (responseData && typeof responseData === 'object') {
    if (responseData.error_msg) {
      message = String(responseData.error_msg);
    } else if (responseData.error) {
      message = String(responseData.error);
    }
    if (responseData.trace_id) {
      traceId = String(responseData.trace_id);
    }
  }
  const startedAt = Number((_config$metadata = config.metadata) === null || _config$metadata === void 0 ? void 0 : _config$metadata.startedAt);
  const durationMs = Number.isFinite(startedAt) ? Math.max(0, Date.now() - startedAt) : undefined;
  pushBounded(networkFailures, {
    time: new Date().toISOString(),
    method: toTrimmedString(config.method).toUpperCase() || 'GET',
    url: sanitizeUrl(config.url || ''),
    status: ((_error$response2 = error.response) === null || _error$response2 === void 0 ? void 0 : _error$response2.status) || null,
    durationMs,
    traceId: truncate(traceId, 80),
    message: truncate(message, MAX_MESSAGE_LENGTH)
  }, MAX_NETWORK_FAILURES);
};
export const recordJsError = ({
  type: _type = 'error',
  message: _message = '',
  source: _source = '',
  stack: _stack = ''
} = {}) => {
  const trimmedMessage = truncate(_message, MAX_MESSAGE_LENGTH);
  if (!trimmedMessage) {
    return;
  }
  pushBounded(jsErrors, {
    time: new Date().toISOString(),
    type: truncate(_type, 40),
    message: trimmedMessage,
    source: truncate(_source, MAX_URL_LENGTH),
    stack: truncate(_stack, MAX_STACK_LENGTH)
  }, MAX_JS_ERRORS);
};
export const readConnectivity = (globalScope = global) => {
  const navigatorRef = (globalScope === null || globalScope === void 0 ? void 0 : globalScope.navigator) || {};
  const connection = navigatorRef.connection || navigatorRef.mozConnection || navigatorRef.webkitConnection || {};
  return {
    online: typeof navigatorRef.onLine === 'boolean' ? navigatorRef.onLine : null,
    effectiveType: toTrimmedString(connection.effectiveType) || '',
    downlink: typeof connection.downlink === 'number' ? connection.downlink : null,
    rtt: typeof connection.rtt === 'number' ? connection.rtt : null
  };
};

/**
 * Session details that are safe to attach automatically. Query values are stripped from URLs.
 */
export const readSessionContext = (globalScope = global) => {
  const locationRef = (globalScope === null || globalScope === void 0 ? void 0 : globalScope.location) || {};
  const navigatorRef = (globalScope === null || globalScope === void 0 ? void 0 : globalScope.navigator) || {};
  const width = globalScope === null || globalScope === void 0 ? void 0 : globalScope.innerWidth;
  const height = globalScope === null || globalScope === void 0 ? void 0 : globalScope.innerHeight;
  let timezone = '';
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch (_unused2) {
    timezone = '';
  }
  return {
    time: new Date().toISOString(),
    href: sanitizeUrl(locationRef.href || ''),
    route: sanitizeUrl(locationRef.hash || locationRef.pathname || ''),
    viewport: typeof width === 'number' && typeof height === 'number' ? `${width}x${height}` : '',
    language: toTrimmedString(navigatorRef.language || ''),
    timezone: toTrimmedString(timezone),
    userAgent: truncate(navigatorRef.userAgent || '', MAX_MESSAGE_LENGTH)
  };
};
const formatNetworkFailure = entry => {
  const parts = [entry.time, entry.method, entry.url || '(unknown url)', entry.status == null ? 'no-status' : `status=${entry.status}`];
  if (entry.durationMs != null) {
    parts.push(`${entry.durationMs}ms`);
  }
  if (entry.traceId) {
    parts.push(`trace=${entry.traceId}`);
  }
  if (entry.message) {
    parts.push(entry.message);
  }
  return parts.join(' | ');
};
const formatJsError = entry => {
  const parts = [entry.time, entry.type, entry.message];
  if (entry.source) {
    parts.push(entry.source);
  }
  const lines = [parts.join(' | ')];
  if (entry.stack) {
    lines.push(entry.stack);
  }
  return lines.join('\n');
};
const formatConnectivity = connectivity => {
  const parts = [`online=${connectivity.online == null ? 'unknown' : connectivity.online}`];
  if (connectivity.effectiveType) {
    parts.push(`type=${connectivity.effectiveType}`);
  }
  if (connectivity.downlink != null) {
    parts.push(`downlink=${connectivity.downlink}`);
  }
  if (connectivity.rtt != null) {
    parts.push(`rtt=${connectivity.rtt}`);
  }
  return parts.join(' | ');
};
const formatSession = session => [`time: ${session.time || ''}`, `href: ${session.href || ''}`, `route: ${session.route || ''}`, `viewport: ${session.viewport || ''}`, `language: ${session.language || ''}`, `timezone: ${session.timezone || ''}`, `userAgent: ${session.userAgent || ''}`].join('\n');

/**
 * Build the opaque diagnostics blob attached to a problem report.
 * Always includes session + connectivity; network/JS sections appear when present.
 */
export const snapshotFeedbackDiagnostics = (globalScope = global) => {
  const session = readSessionContext(globalScope);
  const connectivity = readConnectivity(globalScope);
  const sections = [`session:\n${formatSession(session)}`, `connectivity: ${formatConnectivity(connectivity)}`];
  if (networkFailures.length) {
    sections.push([`networkFailures (${networkFailures.length}):`, ...networkFailures.map(formatNetworkFailure)].join('\n'));
  }
  if (jsErrors.length) {
    sections.push([`jsErrors (${jsErrors.length}):`, ...jsErrors.map(formatJsError)].join('\n\n'));
  }
  return truncate(sections.join('\n\n'), MAX_DIAGNOSTICS_LENGTH);
};
const handleWindowError = event => {
  const error = event === null || event === void 0 ? void 0 : event.error;
  recordJsError({
    type: 'error',
    message: (error === null || error === void 0 ? void 0 : error.message) || (event === null || event === void 0 ? void 0 : event.message) || 'Unknown error',
    source: (event === null || event === void 0 ? void 0 : event.filename) || '',
    stack: (error === null || error === void 0 ? void 0 : error.stack) || ''
  });
};
const handleUnhandledRejection = event => {
  const reason = event === null || event === void 0 ? void 0 : event.reason;
  if (reason && typeof reason === 'object') {
    recordJsError({
      type: 'unhandledrejection',
      message: reason.message || String(reason),
      stack: reason.stack || ''
    });
    return;
  }
  recordJsError({
    type: 'unhandledrejection',
    message: reason == null ? 'Unhandled rejection' : String(reason)
  });
};

/**
 * Install window + axios listeners once. Safe to call repeatedly.
 */
export const installFeedbackDiagnostics = (globalScope = global) => {
  if (listenersInstalled) {
    return;
  }
  listenersInstalled = true;
  if (typeof globalScope.addEventListener === 'function') {
    globalScope.addEventListener('error', handleWindowError);
    globalScope.addEventListener('unhandledrejection', handleUnhandledRejection);
  }
  axiosRequestInterceptorId = axios.interceptors.request.use(config => {
    const nextConfig = config || {};
    nextConfig.metadata = _objectSpread(_objectSpread({}, nextConfig.metadata || {}), {}, {
      startedAt: Date.now()
    });
    return nextConfig;
  });
  axiosResponseInterceptorId = axios.interceptors.response.use(response => response, error => {
    try {
      recordNetworkFailure(error);
    } catch (_unused3) {
      // Diagnostics must never break the original request failure path.
    }
    return Promise.reject(error);
  });
};

/** Test helper — tears down listeners installed by installFeedbackDiagnostics. */
export const uninstallFeedbackDiagnostics = (globalScope = global) => {
  if (!listenersInstalled) {
    return;
  }
  listenersInstalled = false;
  if (typeof globalScope.removeEventListener === 'function') {
    globalScope.removeEventListener('error', handleWindowError);
    globalScope.removeEventListener('unhandledrejection', handleUnhandledRejection);
  }
  if (axiosRequestInterceptorId != null) {
    axios.interceptors.request.eject(axiosRequestInterceptorId);
    axiosRequestInterceptorId = null;
  }
  if (axiosResponseInterceptorId != null) {
    axios.interceptors.response.eject(axiosResponseInterceptorId);
    axiosResponseInterceptorId = null;
  }
  clearFeedbackDiagnostics();
};
export default {
  MAX_DIAGNOSTICS_LENGTH,
  MAX_JS_ERRORS,
  MAX_NETWORK_FAILURES,
  clearFeedbackDiagnostics,
  installFeedbackDiagnostics,
  readConnectivity,
  readSessionContext,
  recordJsError,
  recordNetworkFailure,
  sanitizeUrl,
  snapshotFeedbackDiagnostics,
  uninstallFeedbackDiagnostics
};